// What the screen and the server share about documents: the answers of the REST
// routes and the pure helpers. No imports beyond pure modules, so the screen
// reads it through `@trameria/core/documents-shared`.
export * from "./text-diff";

/** `index`: no text of its own; it orders and nests other documents. */
export type PageKindDTO = "markdown" | "html" | "slide" | "index";

export interface PageDTO {
  id: string;
  key: string;
  title: string;
  kind: PageKindDTO;
  rawQuery: string | null;
  /** The workspace (cloud's) a personal document belongs to. */
  workspaceId: string | null;
  /** The owner of a personal document; null for a workspace's. */
  ownerUserId: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PageListItemDTO extends PageDTO {
  revision: number;
}

export interface PageSectionDTO {
  id: string;
  title: string;
}

export interface PageDetailDTO {
  page: PageDTO;
  revision: number;
  /** The editor generation that wrote the body. */
  generation: number;
  markdown: string | null;
  html: string | null;
  /** The top-level sections of an html / slide body. */
  sections: PageSectionDTO[];
  /**
   * The name to connect the live copy with, or null when it cannot be edited
   * live here (archived, html / slide, written by a newer editor).
   */
  live: string | null;
  /**
   * Why it is read only, when it is (`not_writable`: a shared document this
   * person may read but not change).
   */
  readOnly: null | "archived" | "newer_document" | "not_writable";
  /** The place of a favorite of it (`me` for a personal document). */
  favoritePlace: string;
  /** Whether the viewer's own AI memo on it has anything (the "…" offers it then). */
  hasAgentMemo: boolean;
}

export interface VersionActorDTO {
  userId: string;
  credentialId: string | null;
  /** The person's name now. */
  name: string;
  /** The name of the connection (an MCP client, a device), when there was one. */
  connection: string | null;
}

export interface PageVersionDTO {
  id: string;
  revision: number;
  firstRevision: number;
  restoredFrom: number | null;
  actors: VersionActorDTO[];
  saveCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface PageVersionDetailDTO {
  version: PageVersionDTO & { markdown: string | null; html: string | null };
  base: {
    revision: number;
    markdown: string | null;
    html: string | null;
  } | null;
  /** Who wrote each line of the version (index into `sets`, null for the base's lines). */
  authors: { sets: VersionActorDTO[][]; lines: (number | null)[] };
}

export interface UiAnswerDTO {
  blockId: string;
  userId: string;
  name: string;
  value: unknown;
  updatedAt: string;
}

/** A block id as the editor makes them (`b` and ten characters). */
export const BLOCK_ID_RE = /^b[A-Za-z0-9_-]{10}$/;

/** The largest answer to a ui block (UTF-8 bytes of its JSON). */
export const UI_ANSWER_MAX_BYTES = 16 * 1024;

/** The screen path of a document. */
export const documentPath = (key: string) => `/me/documents/${key}`;

/**
 * Paths of Me's documents a URL names a document by, as ref kinds write paths
 * (`:key` the document's; an index's document is the last key).
 */
export const ME_DOCUMENT_REF_PATHS = [
  "/me/documents/:key",
  "/me/documents/:key/*",
  "/me/documents/index/:key",
  "/me/documents/index/:index/:key",
] as const;

/** Where a document opens: an index opens as its tree. */
export const documentOpenPath = (page: { kind: PageKindDTO; key: string }) =>
  page.kind === "index"
    ? `/me/documents/index/${page.key}`
    : documentPath(page.key);
